import React from "react";
import {
  Box,
  Heading,
  Text,
  Spinner,
  Badge,
  VStack,
  HStack,
  Divider,
  Flex,
  Button,
  useToast,
  IconButton,
} from "@chakra-ui/react";
import { formatDistanceToNow } from "date-fns";
import { DeleteIcon } from "@chakra-ui/icons";

import {
  useGetNotificationsByUserId,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
  useDeleteNotification,
} from "../apis/doctor";
import { useAppSelector } from "../hooks/useAppSelector";
import { useNavigate } from "react-router-dom";

const NotificationPage: React.FC = () => {
  const toast = useToast();
  const { doctorInfo } = useAppSelector((state) => state.auth);
  const userId = doctorInfo?._id;
  const navigate = useNavigate();

  const { data: notifications = [], isError } =
    useGetNotificationsByUserId(userId);

  const { mutate: markAsRead } = useMarkNotificationAsRead(userId);
  const { mutate: markAllAsRead } = useMarkAllNotificationsAsRead(userId);
  const { mutate: deleteNotification } = useDeleteNotification(userId);

  const handleBackToHome = () => {
    navigate("/doctor");
  };

  const handleDelete = (notificationId: string) => {
    deleteNotification(notificationId, {
      onSuccess: () => {
        toast({
          title: "Notification deleted.",
          status: "success",
          isClosable: true,
        });
      },
      onError: () => {
        toast({
          title: "Failed to delete notification.",
          status: "error",
          isClosable: true,
        });
      },
    });
  };

  const handleMarkAll = () => {
    markAllAsRead(undefined, {
      onSuccess: () => {
        toast({
          title: "All notifications marked as read.",
          status: "success",
          isClosable: true,
        });
      },
      onError: () => {
        toast({
          title: "Failed to mark all as read.",
          status: "error",
          isClosable: true,
        });
      },
    });
  };

  if (isError) {
    return (
      <Box p={6}>
        <Text color="red.500">Failed to load notifications.</Text>
      </Box>
    );
  }

  return (
    <Box p={6} maxW="700px" mx="auto">
      <Flex justify="space-between" align="center" mb={4}>
        <Button size="sm" variant="outline" onClick={handleBackToHome}>
          ← Back to Home
        </Button>
      </Flex>

      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="lg">Notifications</Heading>
        {notifications.length > 0 && (
          <Button size="sm" colorScheme="blue" onClick={handleMarkAll}>
            Mark all as read
          </Button>
        )}
      </Flex>

      <Divider mb={6} />

      {notifications.length === 0 ? (
        <Text>No notifications available.</Text>
      ) : (
        <VStack spacing={4} align="stretch">
          {notifications.map((notif) => (
            <Box
              key={notif._id}
              p={4}
              borderWidth={1}
              borderRadius="md"
              bg={notif.isRead ? "gray.50" : "blue.50"}
              _hover={{ bg: "gray.100" }}
              transition="background 0.2s ease"
              position="relative"
              onClick={() => {
                if (!notif.isRead) {
                  markAsRead(notif._id);
                }
              }}
            >
              <HStack justify="space-between" mb={1}>
                <Text fontWeight="bold">{notif.title}</Text>
                <HStack spacing={2}>
                  {!notif.isRead && <Badge colorScheme="blue">New</Badge>}
                  <IconButton
                    aria-label="Delete notification"
                    icon={<DeleteIcon />}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevents triggering markAsRead
                      handleDelete(notif._id);
                    }}
                  />
                </HStack>
              </HStack>
              <Text>{notif.message}</Text>
              <Text fontSize="sm" color="gray.500" mt={2}>
                {formatDistanceToNow(new Date(notif.createdAt), {
                  addSuffix: true,
                })}
              </Text>
            </Box>
          ))}
        </VStack>
      )}
    </Box>
  );
};

export default NotificationPage;
